/**
 * フィールド設計書の保存時バリデーションユーティリティ
 *
 * 削除されたフィールドを参照しているLOOKUP/REFERENCE_TABLEを検出し、
 * 警告表示と参照のカスケード削除を行う
 */

import type { AppData, FieldData, FieldOptions, OptionItem } from "~/components/ui/itemModal/types";

/**
 * nocode が許可するフィールドタイプ (field_creation.py ALLOWED_TYPES 準拠)
 * CALC は「一応許容リストには入れるが後続で除外」のため除外
 */
export const NOCODE_ALLOWED_TYPES = new Set([
  "GROUP",
  "GROUP_SELECT",
  "CHECK_BOX",
  "SUBTABLE",
  "DROP_DOWN",
  "USER_SELECT",
  "RADIO_BUTTON",
  "RICH_TEXT",
  "LINK",
  "REFERENCE_TABLE",
  "TIME",
  "NUMBER",
  "ORGANIZATION_SELECT",
  "FILE",
  "DATETIME",
  "DATE",
  "MULTI_SELECT",
  "SINGLE_LINE_TEXT",
  "MULTI_LINE_TEXT",
]);

/** SUBTABLE 内で使用禁止のフィールドタイプ (constants.py FORBIDDEN_IN_SUBTABLE 準拠) */
export const FORBIDDEN_IN_SUBTABLE = new Set(["LOOKUP", "REFERENCE_TABLE"]);

/** 選択肢系フィールドタイプ (constants.py SELECTION_FIELD_TYPES 準拠) */
export const SELECTION_FIELD_TYPES = new Set([
  "DROP_DOWN",
  "RADIO_BUTTON",
  "CHECK_BOX",
  "MULTI_SELECT",
]);

/** LOOKUP の基底タイプ (kintone 仕様準拠: SINGLE_LINE_TEXT, NUMBER, CALC, LINK) */
export const LOOKUP_BASE_TYPES = new Set(["SINGLE_LINE_TEXT", "NUMBER", "CALC", "LINK"]);

/**
 * LOOKUP fieldMappings のコピー先タイプ → 許可するコピー元タイプの互換マトリクス
 * kintone 公式仕様準拠
 */
export const FIELD_MAPPING_COMPATIBLE_SOURCES: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ["SINGLE_LINE_TEXT", new Set(["SINGLE_LINE_TEXT", "RADIO_BUTTON", "DROP_DOWN"])],
  ["MULTI_LINE_TEXT", new Set(["SINGLE_LINE_TEXT", "MULTI_LINE_TEXT"])],
  ["RICH_TEXT", new Set(["SINGLE_LINE_TEXT", "MULTI_LINE_TEXT", "RICH_TEXT"])],
  ["NUMBER", new Set(["NUMBER", "CALC", "RECORD_NUMBER"])],
  ["DROP_DOWN", new Set(["DROP_DOWN"])],
  ["RADIO_BUTTON", new Set(["RADIO_BUTTON"])],
  ["CHECK_BOX", new Set(["CHECK_BOX"])],
  ["MULTI_SELECT", new Set(["MULTI_SELECT"])],
  ["DATE", new Set(["DATE"])],
  ["TIME", new Set(["TIME"])],
  ["DATETIME", new Set(["DATETIME", "CREATED_TIME", "UPDATED_TIME"])],
  ["USER_SELECT", new Set(["USER_SELECT", "CREATOR", "MODIFIER"])],
  ["ORGANIZATION_SELECT", new Set(["ORGANIZATION_SELECT"])],
  ["GROUP_SELECT", new Set(["GROUP_SELECT"])],
  ["LINK", new Set(["LINK"])],
]);

/**
 * kintone フィールドタイプ → 日本語ラベル（ユーザー向けメッセージ用、kintone公式ヘルプ準拠）
 * 未定義のタイプは元の値をそのまま表示する。
 */
const FIELD_TYPE_LABELS: Record<string, string> = {
  RECORD_NUMBER: "レコード番号",
  SINGLE_LINE_TEXT: "文字列（1行）",
  MULTI_LINE_TEXT: "文字列（複数行）",
  RICH_TEXT: "リッチエディター",
  NUMBER: "数値",
  CALC: "計算",
  DATE: "日付",
  TIME: "時刻",
  DATETIME: "日時",
  RADIO_BUTTON: "ラジオボタン",
  DROP_DOWN: "ドロップダウン",
  CHECK_BOX: "チェックボックス",
  MULTI_SELECT: "複数選択",
  USER_SELECT: "ユーザー選択",
  GROUP_SELECT: "グループ選択",
  ORGANIZATION_SELECT: "組織選択",
  LINK: "リンク",
  CREATED_TIME: "作成日時",
  UPDATED_TIME: "更新日時",
  CREATOR: "作成者",
  MODIFIER: "更新者",
};

/** フィールドタイプを日本語ラベルに変換する（未定義は元の値を返す） */
function formatFieldType(type: string): string {
  return FIELD_TYPE_LABELS[type] ?? type;
}

/**
 * コピー元フィールドタイプを「受け取れる」コピー先フィールドタイプを、日本語ラベルの
 * 「A / B / C」形式で返す（ユーザー向け案内用）。型不整合の修正では、関連アプリ側の
 * コピー元より、設計中アプリ側のコピー先を変えることが多いため、コピー先候補を提示する。
 * kintone 公式の対応表に準拠した FIELD_MAPPING_COMPATIBLE_SOURCES の逆引きで生成する。
 */
function formatCompatibleDestinations(srcType: string): string {
  const dests: string[] = [];
  FIELD_MAPPING_COMPATIBLE_SOURCES.forEach((allowed, destType) => {
    if (allowed.has(srcType)) dests.push(formatFieldType(destType));
  });
  return dests.join(" / ");
}

/** 1アプリあたりのフィールド数上限（システムフィールド除外、バックエンドと一致） */
export const MAX_FIELDS_PER_APP = 240;

/** kintone システムフィールドの数（固定8件、参照用） */
export const SYSTEM_FIELD_COUNT = 8;

/** フィールド名（ラベル）最大文字数 */
export const MAX_FIELD_LABEL_LENGTH = 128;

/** フィールドコード最大文字数 */
export const MAX_FIELD_CODE_LENGTH = 128;

/** kintone 予約語（単独使用不可） */
export const RESERVED_FIELD_CODES = new Set([
  "ステータス",
  "作業者",
  "カテゴリー",
  "__ROOT__",
  "not",
]);

/** kintone システムフィールドコード（ユーザー定義不可） */
export const SYSTEM_FIELD_CODES = new Set([
  "レコード番号",
  "作成者",
  "作成日時",
  "更新者",
  "更新日時",
  "RECORD_NUMBER",
  "__ID__",
  "__REVISION__",
  "CREATOR",
  "CREATED_TIME",
  "MODIFIER",
  "UPDATED_TIME",
]);

/**
 * kintone フィールドコードで使用禁止の文字パターン
 * 括弧類、@, +, ~, #, %, &, ', =, |, ^, *, ;, :, ?, スペース
 */
const INVALID_FIELD_CODE_CHARS = /[()「」[\]【】{}@+~#%&'=|^*;:?\s]/;

/**
 * kintone フィールドコード形式のバリデーション
 *
 * @returns エラーメッセージ（null ならバリデーション成功）
 */
export function validateFieldCode(
  fieldCode: string,
  fieldId: string,
  appId: string,
  allFields: FieldData[],
): string | null {
  // 先頭・末尾の空白チェック（trim前に実施）
  if (fieldCode !== fieldCode.trim()) {
    return "フィールドコードの先頭・末尾に空白は使用できません";
  }

  const trimmed = fieldCode.trim();

  // 空チェック
  if (!trimmed) {
    return "フィールドコードは空にできません";
  }

  // 文字数チェック
  if (trimmed.length > MAX_FIELD_CODE_LENGTH) {
    return `フィールドコードは${MAX_FIELD_CODE_LENGTH}文字以内にしてください`;
  }

  // 先頭半角数字チェック
  if (/^[0-9]/.test(trimmed)) {
    return "フィールドコードの先頭に半角数字は使用できません";
  }

  // 禁止文字チェック
  const invalidMatch = trimmed.match(INVALID_FIELD_CODE_CHARS);
  if (invalidMatch) {
    return `フィールドコードに使用できない文字「${invalidMatch[0]}」が含まれています`;
  }

  // NFKC 正規化チェック（全角英数字→半角への正規化が必要な場合を検出）
  const normalized = trimmed.normalize("NFKC");
  if (normalized !== trimmed) {
    return `フィールドコードに正規化が必要な文字が含まれています（推奨: 「${normalized}」）`;
  }

  // システムフィールドコードチェック
  if (SYSTEM_FIELD_CODES.has(trimmed)) {
    return `「${trimmed}」はシステムフィールドのため使用できません`;
  }

  // 予約語チェック
  if (RESERVED_FIELD_CODES.has(trimmed)) {
    return `「${trimmed}」は予約語のため使用できません`;
  }

  // 同一アプリ内重複チェック
  const duplicate = allFields.find(
    (f) => f.app_id === appId && f.フィールドコード === trimmed && f["id"] !== fieldId,
  );
  if (duplicate) {
    return `フィールドコード「${trimmed}」は同一アプリ内で重複しています`;
  }

  return null;
}

/**
 * 指定アプリのフィールド総数を計算する（システムフィールド除外）
 *
 * カウント対象:
 * - properties内の各フィールド（SUBTABLE自体も含む）
 * - SUBTABLEの子フィールド
 *
 * バックエンド（_MAX_FIELDS_PER_APP = 240）と同じカウント方式
 */
export function countFieldsForApp(fieldsForApp: FieldData[]): number {
  let count = 0;

  for (const field of fieldsForApp) {
    // SUBTABLE自体をカウント
    count += 1;

    // SUBTABLEの子フィールドをカウント
    if (field.フィールドタイプ === "SUBTABLE" && field.options?.fields) {
      count += Object.keys(field.options.fields).length;
    }
  }

  return count;
}

/**
 * 無効な参照の情報
 */
export interface InvalidReference {
  /** 参照元のフィールド */
  sourceField: FieldData;
  /** 参照タイプ (LOOKUP | REFERENCE_TABLE) */
  referenceType: "LOOKUP" | "REFERENCE_TABLE";
  /** 参照先のアプリ名 */
  targetAppName: string;
  /** 参照先のフィールドコード（削除されたもの） */
  deletedFieldCodes: string[];
  /** 詳細メッセージ */
  message: string;
  /** 致命的な参照エラーか（根幹の参照が壊れている） */
  isCritical: boolean;
  /** 致命的エラーの理由 */
  criticalReason: string | undefined;
}

/**
 * 削除されたフィールドの情報
 */
export interface DeletedFieldInfo {
  app_id: string;
  fieldCode: string;
  fieldName: string;
}

/**
 * バリデーション結果
 */
export interface ValidationResult {
  /** バリデーションが成功したか */
  isValid: boolean;
  /** 保存をブロックする（ユーザー修正が必要な）エラー */
  blockingErrors: string[];
  /** 無効な参照のリスト（全て） */
  invalidReferences: InvalidReference[];
  /** 致命的な参照エラー（フィールド削除が必要） */
  criticalReferences: InvalidReference[];
  /** 安全にクリーンアップ可能な参照 */
  safeCleanableReferences: InvalidReference[];
  /** 削除されたフィールドのリスト */
  deletedFields: DeletedFieldInfo[];
  /** 警告メッセージ */
  warnings: string[];
  /** 保存をブロックすべきか（致命的エラーがある場合） */
  shouldBlockSave: boolean;
  /**
   * 自動補完済みの正規化データ（LINK protocol 補完, NUMBER digit 正規化などが適用済み）
   * 呼び出し元は保存前にこのデータを Redux state に反映すること
   */
  normalizedData: FieldData[];
}

/**
 * クリーンアップ結果
 */
export interface CleanupResult {
  /** クリーンアップ後のデータ */
  data: FieldData[];
  /** 削除が必要なフィールド（ユーザー確認待ち） */
  fieldsToDelete: FieldData[];
  /** 安全にクリーンアップされたフィールド */
  safelyCleanedFields: FieldData[];
}

/**
 * 現在のデータと元データを比較して削除されたフィールドを特定
 */
export function findDeletedFields(
  currentData: FieldData[],
  originalData: FieldData[],
): DeletedFieldInfo[] {
  const currentFieldIds = new Set(currentData.map((f) => f["id"]));

  return originalData
    .filter((f) => !currentFieldIds.has(f["id"]))
    .map((f) => ({
      app_id: f.app_id,
      fieldCode: f.フィールドコード,
      fieldName: f.フィールド名,
    }));
}

/**
 * アプリIDからアプリ名を取得
 */
function getAppNameById(appData: AppData[], appId: string): string | null {
  const app = appData.find((a) => a.app_id === appId);
  return app?.app_name ?? null;
}

/**
 * LOOKUPフィールドの参照情報（致命的/安全の区別付き）
 */
interface LookupReferenceInfo {
  appName: string;
  /** 根幹の参照フィールド（これが壊れたらLOOKUP自体が無効） */
  criticalFieldCode: string | null;
  /** 補助的な参照フィールド（安全に除去可能） */
  auxiliaryFieldCodes: string[];
  /** 全てのフィールドコード */
  allFieldCodes: string[];
}

/**
 * LOOKUPフィールドが参照しているフィールドコードを取得
 */
function getLookupReferencedFields(options: FieldOptions): LookupReferenceInfo | null {
  const lookup = options.lookup;
  if (!lookup?.relatedApp?.app) {
    return null;
  }

  const criticalFieldCode = lookup.relatedKeyField || null;
  const auxiliaryFieldCodes: string[] = [];

  // fieldMappings の relatedField（補助的）
  if (lookup.fieldMappings) {
    lookup.fieldMappings.forEach((mapping) => {
      if (mapping.relatedField) {
        auxiliaryFieldCodes.push(mapping.relatedField);
      }
    });
  }

  // lookupPickerFields（補助的）
  if (lookup.lookupPickerFields) {
    auxiliaryFieldCodes.push(...lookup.lookupPickerFields);
  }

  const allFieldCodes = criticalFieldCode
    ? [criticalFieldCode, ...auxiliaryFieldCodes]
    : auxiliaryFieldCodes;

  return {
    appName: lookup.relatedApp.app,
    criticalFieldCode,
    auxiliaryFieldCodes: Array.from(new Set(auxiliaryFieldCodes)),
    allFieldCodes: Array.from(new Set(allFieldCodes)),
  };
}

/**
 * REFERENCE_TABLEフィールドの参照情報（致命的/安全の区別付き）
 */
interface ReferenceTableReferenceInfo {
  appName: string;
  /** 根幹の参照フィールド（これが壊れたらREFERENCE_TABLE自体が無効） */
  criticalFieldCode: string | null;
  /** 補助的な参照フィールド（安全に除去可能） */
  auxiliaryFieldCodes: string[];
  /** 全てのフィールドコード */
  allFieldCodes: string[];
}

/**
 * REFERENCE_TABLEフィールドが参照しているフィールドコードを取得
 */
function getReferenceTableReferencedFields(
  options: FieldOptions,
): ReferenceTableReferenceInfo | null {
  const refTable = options.referenceTable;
  if (!refTable?.relatedApp?.app) {
    return null;
  }

  // condition.relatedField は根幹の参照
  const criticalFieldCode = refTable.condition?.relatedField || null;

  // displayFields は補助的
  const auxiliaryFieldCodes: string[] = [];
  if (refTable.displayFields) {
    auxiliaryFieldCodes.push(...refTable.displayFields);
  }

  const allFieldCodes = criticalFieldCode
    ? [criticalFieldCode, ...auxiliaryFieldCodes]
    : auxiliaryFieldCodes;

  return {
    appName: refTable.relatedApp.app,
    criticalFieldCode,
    auxiliaryFieldCodes: Array.from(new Set(auxiliaryFieldCodes)),
    allFieldCodes: Array.from(new Set(allFieldCodes)),
  };
}

/**
 * 削除されたフィールドを参照しているフィールドを検出
 */
export function findInvalidReferences(
  currentData: FieldData[],
  deletedFields: DeletedFieldInfo[],
  appData: AppData[],
): InvalidReference[] {
  const invalidReferences: InvalidReference[] = [];

  // 削除されたフィールドをアプリ別にグループ化
  // アプリIDでもアプリ名でも検索できるようにする
  const deletedByAppIdentifier = new Map<string, Set<string>>();
  deletedFields.forEach((df) => {
    const appName = getAppNameById(appData, df.app_id);
    const fieldCodes = deletedByAppIdentifier.get(df.app_id) || new Set();
    fieldCodes.add(df.fieldCode);
    // アプリIDでも検索できるようにする
    deletedByAppIdentifier.set(df.app_id, fieldCodes);
    // アプリ名でも検索できるようにする
    if (appName) {
      deletedByAppIdentifier.set(appName, fieldCodes);
    }
  });

  // 各フィールドをチェック
  currentData.forEach((field) => {
    if (!field.options) return;

    // LOOKUP のチェック
    if (field.フィールドタイプ === "LOOKUP") {
      const refs = getLookupReferencedFields(field.options);
      if (refs) {
        const deletedInTargetApp = deletedByAppIdentifier.get(refs.appName);
        if (deletedInTargetApp) {
          const brokenRefs = refs.allFieldCodes.filter((code) => deletedInTargetApp.has(code));
          if (brokenRefs.length > 0) {
            // 根幹の参照（relatedKeyField）が削除されたかチェック
            const isCritical =
              refs.criticalFieldCode !== null && deletedInTargetApp.has(refs.criticalFieldCode);

            invalidReferences.push({
              sourceField: field,
              referenceType: "LOOKUP",
              targetAppName: refs.appName,
              deletedFieldCodes: brokenRefs,
              message: `LOOKUP「${field.フィールド名}」が参照している「${refs.appName}」の以下のフィールドが削除されました: ${brokenRefs.join(", ")}`,
              isCritical,
              criticalReason: isCritical
                ? `参照キー「${refs.criticalFieldCode}」が削除されたため、このLOOKUPフィールドは無効です`
                : undefined,
            });
          }
        }
      }
    }

    // REFERENCE_TABLE のチェック
    if (field.フィールドタイプ === "REFERENCE_TABLE") {
      const refs = getReferenceTableReferencedFields(field.options);
      if (refs) {
        const deletedInTargetApp = deletedByAppIdentifier.get(refs.appName);
        if (deletedInTargetApp) {
          const brokenRefs = refs.allFieldCodes.filter((code) => deletedInTargetApp.has(code));
          if (brokenRefs.length > 0) {
            // 根幹の参照（condition.relatedField）が削除されたかチェック
            const isCritical =
              refs.criticalFieldCode !== null && deletedInTargetApp.has(refs.criticalFieldCode);

            invalidReferences.push({
              sourceField: field,
              referenceType: "REFERENCE_TABLE",
              targetAppName: refs.appName,
              deletedFieldCodes: brokenRefs,
              message: `関連レコード「${field.フィールド名}」が参照している「${refs.appName}」の以下のフィールドが削除されました: ${brokenRefs.join(", ")}`,
              isCritical,
              criticalReason: isCritical
                ? `条件フィールド「${refs.criticalFieldCode}」が削除されたため、この関連レコードフィールドは無効です`
                : undefined,
            });
          }
        }
      }
    }
  });

  return invalidReferences;
}

/**
 * LOOKUP/REFERENCE_TABLEが存在しないフィールドを参照していないかチェック
 * （新規追加や既存の壊れた参照を検出）
 */
export function findOrphanedReferences(
  currentData: FieldData[],
  appData: AppData[],
): InvalidReference[] {
  const invalidReferences: InvalidReference[] = [];

  // kintone system fields exist even if excluded from the field design doc
  const systemFieldCodes = new Set([
    "レコード番号",
    "作成者",
    "作成日時",
    "更新者",
    "更新日時",
    "ステータス",
    "作業者",
    "カテゴリー",
  ]);

  // アプリの識別子（ID or 名前）からフィールドコードを取得するマップを作成
  // LOOKUPのrelatedApp.appにはIDが入ることもあれば名前が入ることもあるため、両方に対応
  const fieldCodesByAppIdentifier = new Map<string, Set<string>>();
  appData.forEach((app) => {
    const fieldsForApp = currentData.filter((f) => f.app_id === app.app_id);
    const fieldCodes = new Set(fieldsForApp.map((f) => f.フィールドコード));
    systemFieldCodes.forEach((code) => {
      fieldCodes.add(code);
    });
    // アプリ名でもアプリIDでも検索できるようにする
    fieldCodesByAppIdentifier.set(app.app_name, fieldCodes);
    fieldCodesByAppIdentifier.set(app.app_id, fieldCodes);
  });

  // 各フィールドをチェック
  currentData.forEach((field) => {
    if (!field.options) return;

    // LOOKUP のチェック
    if (field.フィールドタイプ === "LOOKUP") {
      const refs = getLookupReferencedFields(field.options);
      if (refs?.appName) {
        const validFieldCodes = fieldCodesByAppIdentifier.get(refs.appName);
        if (!validFieldCodes) {
          // 参照先アプリが存在しない → 常に致命的
          invalidReferences.push({
            sourceField: field,
            referenceType: "LOOKUP",
            targetAppName: refs.appName,
            deletedFieldCodes: [`アプリ「${refs.appName}」が存在しません`],
            message: `LOOKUP「${field.フィールド名}」が参照しているアプリ「${refs.appName}」が存在しません`,
            isCritical: true,
            criticalReason: `参照先アプリ「${refs.appName}」が存在しないため、このLOOKUPフィールドは無効です`,
          });
        } else {
          // 存在しないフィールドを参照していないかチェック
          const invalidFields = refs.allFieldCodes.filter(
            (code: string) => code && !validFieldCodes.has(code),
          );
          if (invalidFields.length > 0) {
            // 根幹の参照（relatedKeyField）が存在しないかチェック
            const isCritical =
              refs.criticalFieldCode !== null && !validFieldCodes.has(refs.criticalFieldCode);

            invalidReferences.push({
              sourceField: field,
              referenceType: "LOOKUP",
              targetAppName: refs.appName,
              deletedFieldCodes: invalidFields,
              message: `LOOKUP「${field.フィールド名}」が参照している「${refs.appName}」の以下のフィールドが存在しません: ${invalidFields.join(", ")}`,
              isCritical,
              criticalReason: isCritical
                ? `参照キー「${refs.criticalFieldCode}」が存在しないため、このLOOKUPフィールドは無効です`
                : undefined,
            });
          }
        }
      }
    }

    // REFERENCE_TABLE のチェック
    if (field.フィールドタイプ === "REFERENCE_TABLE") {
      const refs = getReferenceTableReferencedFields(field.options);
      if (refs?.appName) {
        const validFieldCodes = fieldCodesByAppIdentifier.get(refs.appName);
        if (!validFieldCodes) {
          // 参照先アプリが存在しない → 常に致命的
          invalidReferences.push({
            sourceField: field,
            referenceType: "REFERENCE_TABLE",
            targetAppName: refs.appName,
            deletedFieldCodes: [`アプリ「${refs.appName}」が存在しません`],
            message: `関連レコード「${field.フィールド名}」が参照しているアプリ「${refs.appName}」が存在しません`,
            isCritical: true,
            criticalReason: `参照先アプリ「${refs.appName}」が存在しないため、この関連レコードフィールドは無効です`,
          });
        } else {
          // 存在しないフィールドを参照していないかチェック
          const invalidFields = refs.allFieldCodes.filter(
            (code: string) => code && !validFieldCodes.has(code),
          );
          if (invalidFields.length > 0) {
            // 根幹の参照（condition.relatedField）が存在しないかチェック
            const isCritical =
              refs.criticalFieldCode !== null && !validFieldCodes.has(refs.criticalFieldCode);

            invalidReferences.push({
              sourceField: field,
              referenceType: "REFERENCE_TABLE",
              targetAppName: refs.appName,
              deletedFieldCodes: invalidFields,
              message: `関連レコード「${field.フィールド名}」が参照している「${refs.appName}」の以下のフィールドが存在しません: ${invalidFields.join(", ")}`,
              isCritical,
              criticalReason: isCritical
                ? `条件フィールド「${refs.criticalFieldCode}」が存在しないため、この関連レコードフィールドは無効です`
                : undefined,
            });
          }
        }
      }
    }
  });

  return invalidReferences;
}

type ConditionKeyCategory = "number" | "string" | "unsupported";

function _get_condition_key_category(fieldType: string | undefined): ConditionKeyCategory {
  const t = String(fieldType || "").trim();
  if (!t) return "unsupported";
  if (t === "NUMBER" || t === "CALC" || t === "RECORD_NUMBER") return "number";
  if (t === "SINGLE_LINE_TEXT" || t === "LINK") return "string";
  return "unsupported";
}

function _validate_relation_field_requirements(
  currentData: FieldData[],
  appData: AppData[],
): string[] {
  const blockingErrors: string[] = [];

  const systemFieldTypes: Record<string, string> = {
    レコード番号: "RECORD_NUMBER",
    作成者: "CREATOR",
    作成日時: "CREATED_TIME",
    更新者: "MODIFIER",
    更新日時: "UPDATED_TIME",
    ステータス: "STATUS",
    作業者: "STATUS_ASSIGNEE",
    カテゴリー: "CATEGORY",
  };

  const appIdByIdentifier = new Map<string, string>();
  appData.forEach((app) => {
    appIdByIdentifier.set(app.app_id, app.app_id);
    appIdByIdentifier.set(app.app_name, app.app_id);
  });

  const fieldByAppAndCode = new Map<string, Map<string, FieldData>>();
  appData.forEach((app) => {
    const fieldsForApp = currentData.filter((f) => f.app_id === app.app_id);
    const byCode = new Map<string, FieldData>();
    for (const f of fieldsForApp) {
      byCode.set(f.フィールドコード, f);
    }
    fieldByAppAndCode.set(app.app_id, byCode);
  });

  const resolveAppId = (identifier: string | undefined): string | null => {
    const key = String(identifier || "").trim();
    if (!key) return null;
    return appIdByIdentifier.get(key) ?? null;
  };

  const inferLookupBaseType = (lookupField: FieldData): string | null => {
    const lookup = lookupField.options?.lookup;
    if (!lookup?.relatedApp?.app || !lookup.relatedKeyField) return null;
    const relatedAppId = resolveAppId(lookup.relatedApp.app);
    if (!relatedAppId) return null;
    const key = String(lookup.relatedKeyField).trim();
    const relatedField = fieldByAppAndCode.get(relatedAppId)?.get(key);
    if (!relatedField) return null;
    const relatedType = relatedField.フィールドタイプ;
    if (relatedType === "NUMBER" || relatedType === "RECORD_NUMBER" || relatedType === "CALC")
      return "NUMBER";
    if (relatedType === "SINGLE_LINE_TEXT" || relatedType === "LINK") return "SINGLE_LINE_TEXT";
    return null;
  };

  const getFieldType = (appId: string, code: string): string | null => {
    const c = String(code || "").trim();
    if (!c) return null;
    const sys = systemFieldTypes[c];
    if (sys) return sys;
    const f = fieldByAppAndCode.get(appId)?.get(c);
    if (!f) return null;
    if (f.フィールドタイプ === "LOOKUP") {
      return inferLookupBaseType(f) ?? "LOOKUP";
    }
    return f.フィールドタイプ;
  };

  currentData.forEach((field) => {
    if (!field.options) return;

    if (field.フィールドタイプ === "LOOKUP") {
      const lookup = field.options.lookup;
      if (!lookup) return;
      const picker = Array.isArray(lookup.lookupPickerFields) ? lookup.lookupPickerFields : [];
      if (picker.length === 0) {
        blockingErrors.push(
          `LOOKUP「${field.フィールド名}」: lookupPickerFields は空にできません（最低1件必要）`,
        );
      }
    }

    if (field.フィールドタイプ === "REFERENCE_TABLE") {
      const rt = field.options.referenceTable;
      if (!rt) return;
      const display = Array.isArray(rt.displayFields) ? rt.displayFields : [];
      if (display.length === 0) {
        blockingErrors.push(
          `関連レコード「${field.フィールド名}」: displayFields は空にできません（最低1件必要）`,
        );
      }

      const relatedAppId = resolveAppId(rt.relatedApp?.app);
      if (!relatedAppId) return;

      // displayFields の参照先フィールド存在チェック
      for (const code of display) {
        if (!fieldByAppAndCode.get(relatedAppId)?.has(code) && !(code in systemFieldTypes)) {
          blockingErrors.push(
            `関連レコード「${field.フィールド名}」: displayFields のフィールド「${code}」が参照先アプリに存在しません`,
          );
        }
      }

      const cond = rt.condition || ({} as Record<string, unknown>);
      const localCode = String(cond.field || "").trim();
      const relatedCode = String(cond.relatedField || "").trim();
      if (!localCode || !relatedCode) return;

      const localType = getFieldType(field.app_id, localCode);
      const relatedType = getFieldType(relatedAppId, relatedCode);
      if (!localType || !relatedType) return;

      const left = _get_condition_key_category(localType);
      const right = _get_condition_key_category(relatedType);
      if (left === "unsupported" || right === "unsupported") {
        blockingErrors.push(
          `関連レコード「${field.フィールド名}」: condition のキーは NUMBER / SINGLE_LINE_TEXT / LINK / CALC のみ対応です（${localCode}:${localType} / ${relatedCode}:${relatedType}）`,
        );
      } else if (left !== right) {
        blockingErrors.push(
          `関連レコード「${field.フィールド名}」: condition.field と condition.relatedField の型が一致していません（${localCode}:${localType} / ${relatedCode}:${relatedType}）`,
        );
      } else if (localType === "LINK" && relatedType === "LINK") {
        // LINK protocol must match when both sides specify protocol
        const localFieldData = fieldByAppAndCode.get(field.app_id)?.get(localCode);
        const relatedFieldData = fieldByAppAndCode.get(relatedAppId)?.get(relatedCode);
        const lp = String(localFieldData?.options?.protocol || "").trim();
        const rp = String(relatedFieldData?.options?.protocol || "").trim();
        if (lp && rp && lp !== rp) {
          blockingErrors.push(
            `関連レコード「${field.フィールド名}」: LINK プロトコルが一致しません（${localCode}:${lp} / ${relatedCode}:${rp}）`,
          );
        }
      }
    }

    if (field.フィールドタイプ === "LOOKUP") {
      const lookup = field.options.lookup;
      if (!lookup) return;
      const relatedAppId = resolveAppId(lookup.relatedApp?.app);
      if (!relatedAppId) return;

      // lookupPickerFields の参照先フィールド存在チェック
      const picker = Array.isArray(lookup.lookupPickerFields) ? lookup.lookupPickerFields : [];
      for (const code of picker) {
        if (!fieldByAppAndCode.get(relatedAppId)?.has(code) && !(code in systemFieldTypes)) {
          blockingErrors.push(
            `LOOKUP「${field.フィールド名}」: lookupPickerFields のフィールド「${code}」が参照先アプリに存在しません`,
          );
        }
      }

      // fieldMappings[].relatedField の参照先フィールド存在チェック
      const mappings = Array.isArray(lookup.fieldMappings) ? lookup.fieldMappings : [];
      for (const mapping of mappings) {
        const relatedField = String(mapping.relatedField || "").trim();
        if (
          relatedField &&
          !fieldByAppAndCode.get(relatedAppId)?.has(relatedField) &&
          !(relatedField in systemFieldTypes)
        ) {
          blockingErrors.push(
            `LOOKUP「${field.フィールド名}」: 「他のフィールドのコピー」の参照元フィールド「${relatedField}」が参照先アプリに存在しません`,
          );
        }
      }

      // fieldMappings の型互換性チェック（コピー先タイプ vs コピー元タイプ）
      for (const mapping of mappings) {
        const destCode = String(mapping.field || "").trim();
        const srcCode = String(mapping.relatedField || "").trim();
        if (!destCode || !srcCode) continue;

        const destType = getFieldType(field.app_id, destCode);
        const srcType = getFieldType(relatedAppId, srcCode);
        if (!destType || !srcType) continue;

        const allowed = FIELD_MAPPING_COMPATIBLE_SOURCES.get(destType);
        if (!allowed) continue;

        if (!allowed.has(srcType)) {
          // LINK 同士の場合はプロトコル一致もチェック
          blockingErrors.push(
            `ルックアップ「${field.フィールド名}」：\n　コピー元「${srcCode}」（${formatFieldType(srcType)}）\n　→ コピー先「${destCode}」（${formatFieldType(destType)}）にはコピーできません\n　　コピー先を次のいずれかに変更: ${formatCompatibleDestinations(srcType)}`,
          );
          continue;
        }

        // LINK 同士の場合はプロトコル一致チェック
        if (destType === "LINK" && srcType === "LINK") {
          const destField = fieldByAppAndCode.get(field.app_id)?.get(destCode);
          const srcField = fieldByAppAndCode.get(relatedAppId)?.get(srcCode);
          const dp = String(destField?.options?.protocol || "").trim();
          const sp = String(srcField?.options?.protocol || "").trim();
          if (dp && sp && dp !== sp) {
            blockingErrors.push(
              `LOOKUP「${field.フィールド名}」: 「他のフィールドのコピー」のLINKプロトコルが一致しません（コピー先「${destCode}」:${dp} / コピー元「${srcCode}」:${sp}）`,
            );
          }
        }
      }
    }
  });

  return blockingErrors;
}

/**
 * フィールドオプションの kintone デプロイ時バリデーション
 *
 * @param fieldType - フィールドタイプ
 * @param options - フィールドオプション
 * @param fieldLabel - フィールドラベル（エラーメッセージに使用）
 * @param prefix - エラーメッセージのプレフィックス（例: "テーブル「X」内の"）
 * @returns blockingError 文字列の配列
 */
export function validateFieldOptions(
  fieldType: string,
  options: FieldOptions | undefined,
  fieldLabel: string,
  prefix?: string,
): string[] {
  const prefix_ = prefix ?? "";
  const fieldId = `${prefix_}「${fieldLabel}」`;
  const errors: string[] = [];

  if (fieldType === "SINGLE_LINE_TEXT") {
    // minLength バリデーション
    if (
      options?.minLength !== undefined &&
      options.minLength !== null &&
      options.minLength !== ""
    ) {
      const val = Number(options.minLength);
      if (Number.isNaN(val) || val < 0 || !Number.isInteger(val)) {
        errors.push(`${fieldId}: minLength は正の整数でなければなりません`);
      }
    }

    // maxLength バリデーション
    if (
      options?.maxLength !== undefined &&
      options.maxLength !== null &&
      options.maxLength !== ""
    ) {
      const val = Number(options.maxLength);
      if (Number.isNaN(val) || val < 0 || !Number.isInteger(val)) {
        errors.push(`${fieldId}: maxLength は正の整数でなければなりません`);
      }
    }

    // minLength ≤ maxLength チェック（両方設定されていて有効な数値の場合のみ）
    if (
      options?.minLength !== undefined &&
      options.minLength !== null &&
      options.minLength !== "" &&
      options?.maxLength !== undefined &&
      options.maxLength !== null &&
      options.maxLength !== ""
    ) {
      const minVal = Number(options.minLength);
      const maxVal = Number(options.maxLength);
      if (!Number.isNaN(minVal) && !Number.isNaN(maxVal) && minVal > maxVal) {
        errors.push(
          `${fieldId}: minLength（${options.minLength}）が maxLength（${options.maxLength}）を超えています`,
        );
      }
    }

    // defaultValue 長さ ≤ maxLength チェック
    if (
      options?.maxLength !== undefined &&
      options.maxLength !== null &&
      options.maxLength !== "" &&
      options?.defaultValue !== undefined &&
      options.defaultValue !== null &&
      options.defaultValue !== ""
    ) {
      const maxVal = Number(options.maxLength);
      if (!Number.isNaN(maxVal) && options.defaultValue.length > maxVal) {
        errors.push(
          `${fieldId}: デフォルト値の文字数（${options.defaultValue.length}）が maxLength（${options.maxLength}）を超えています`,
        );
      }
    }
  }

  if (fieldType === "NUMBER") {
    // minValue バリデーション
    if (options?.minValue !== undefined && options.minValue !== null && options.minValue !== "") {
      if (Number.isNaN(Number(options.minValue))) {
        errors.push(`${fieldId}: minValue は有効な数値でなければなりません`);
      }
    }

    // maxValue バリデーション
    if (options?.maxValue !== undefined && options.maxValue !== null && options.maxValue !== "") {
      if (Number.isNaN(Number(options.maxValue))) {
        errors.push(`${fieldId}: maxValue は有効な数値でなければなりません`);
      }
    }

    // minValue ≤ maxValue チェック（両方設定されていて有効な数値の場合のみ）
    if (
      options?.minValue !== undefined &&
      options.minValue !== null &&
      options.minValue !== "" &&
      options?.maxValue !== undefined &&
      options.maxValue !== null &&
      options.maxValue !== ""
    ) {
      const minVal = Number(options.minValue);
      const maxVal = Number(options.maxValue);
      if (!Number.isNaN(minVal) && !Number.isNaN(maxVal) && minVal > maxVal) {
        errors.push(
          `${fieldId}: minValue（${options.minValue}）が maxValue（${options.maxValue}）を超えています`,
        );
      }
    }

    // displayScale バリデーション（digit とは独立した設定）
    if (
      options?.displayScale !== undefined &&
      options.displayScale !== null &&
      options.displayScale !== ""
    ) {
      const ds = Number(options.displayScale);
      if (Number.isNaN(ds) || ds < 0 || !Number.isInteger(ds)) {
        errors.push(`${fieldId}: displayScale は非負の整数でなければなりません`);
      }
    }
  }

  if (SELECTION_FIELD_TYPES.has(fieldType)) {
    // defaultSelectedOptions の各 ID が options に存在するかチェック
    if (
      options?.defaultSelectedOptions &&
      Array.isArray(options.defaultSelectedOptions) &&
      options.defaultSelectedOptions.length > 0
    ) {
      const fieldOptions = options.options;
      if (Array.isArray(fieldOptions)) {
        // UI形式: OptionItem[] — id で検索
        const optionIdSet = new Set((fieldOptions as OptionItem[]).map((item) => item.id));
        for (const id of options.defaultSelectedOptions) {
          if (!optionIdSet.has(id)) {
            errors.push(`${fieldId}: 初期値「${id}」が選択肢に存在しません`);
          }
        }
      } else if (fieldOptions && typeof fieldOptions === "object") {
        // kintone形式: Record<string, {...}> — key で検索
        const optionKeys = fieldOptions as Record<string, unknown>;
        for (const id of options.defaultSelectedOptions) {
          if (!(id in optionKeys)) {
            errors.push(`${fieldId}: 初期値「${id}」が選択肢に存在しません`);
          }
        }
      }

      // DROP_DOWN / RADIO_BUTTON は defaultSelectedOptions が最大1件
      if (
        (fieldType === "DROP_DOWN" || fieldType === "RADIO_BUTTON") &&
        options.defaultSelectedOptions.length > 1
      ) {
        errors.push(`${fieldId}: 単一選択フィールドの初期値は1件までです`);
      }
    }

    // order バリデーション（RADIO_BUTTON, CHECK_BOX, MULTI_SELECT）
    if (
      (fieldType === "RADIO_BUTTON" || fieldType === "CHECK_BOX" || fieldType === "MULTI_SELECT") &&
      options?.order !== undefined &&
      options.order !== null &&
      options.order !== ""
    ) {
      if (options.order !== "VERTICAL" && options.order !== "HORIZONTAL") {
        errors.push(`${fieldId}: order は "VERTICAL" または "HORIZONTAL" でなければなりません`);
      }
    }
  }

  if (fieldType === "FILE") {
    const thumbnailSize = options?.["thumbnailSize"];
    if (thumbnailSize !== undefined && thumbnailSize !== null && thumbnailSize !== "") {
      const validSizes = new Set(["50x50", "150x150", "250x250", "500x500"]);
      if (!validSizes.has(String(thumbnailSize))) {
        errors.push(
          `${fieldId}: thumbnailSize「${thumbnailSize}」は無効です（有効値: 50x50, 150x150, 250x250, 500x500）`,
        );
      }
    }
  }

  return errors;
}

/**
 * 保存前バリデーションを実行
 */
export function validateBeforeSave(
  currentData: FieldData[],
  originalData: FieldData[],
  appData: AppData[],
): ValidationResult {
  // Redux state への直接ミューテーションを避けるためディープクローンを使用
  // LINK protocol 補完・NUMBER digit 正規化などはクローン上で行い、
  // 結果を normalizedData として返す
  const data: FieldData[] = structuredClone(currentData);

  // 削除されたフィールドを特定
  const deletedFields = findDeletedFields(data, originalData);

  // 削除によって無効になる参照を検出
  const referencesInvalidatedByDeletion =
    deletedFields.length > 0 ? findInvalidReferences(data, deletedFields, appData) : [];

  // 存在しないフィールドへの参照（孤立した参照）を検出
  const orphanedReferences = findOrphanedReferences(data, appData);

  // 重複を除いた無効な参照リストを作成
  const allInvalidRefIds = new Set<string>();
  const invalidReferences: InvalidReference[] = [];

  // 削除による無効参照を追加
  referencesInvalidatedByDeletion.forEach((ref) => {
    const refId = `${ref.sourceField["id"]}-${ref.referenceType}`;
    if (!allInvalidRefIds.has(refId)) {
      allInvalidRefIds.add(refId);
      invalidReferences.push(ref);
    }
  });

  // 孤立した参照を追加（削除による無効参照と重複しないもの）
  orphanedReferences.forEach((ref) => {
    const refId = `${ref.sourceField["id"]}-${ref.referenceType}`;
    if (!allInvalidRefIds.has(refId)) {
      allInvalidRefIds.add(refId);
      invalidReferences.push(ref);
    }
  });

  const blockingErrors = _validate_relation_field_requirements(data, appData);

  // フィールドコードの kintone 形式バリデーション（保存時の最終防衛線）
  for (const field of data) {
    const error = validateFieldCode(field.フィールドコード, field["id"], field.app_id, data);
    if (error) {
      blockingErrors.push(`「${field.フィールド名}」のフィールドコード: ${error}`);
    }
  }

  // nocode 許可タイプチェック（LOOKUP は nocode がサポートするため特別許可）
  const SYSTEM_FIELD_TYPES_TO_SKIP = new Set(["RECORD_NUMBER"]);
  for (const field of data) {
    const ft = field.フィールドタイプ;
    if (SYSTEM_FIELD_TYPES_TO_SKIP.has(ft)) continue;
    if (ft === "LOOKUP") continue;
    if (!NOCODE_ALLOWED_TYPES.has(ft)) {
      blockingErrors.push(
        `「${field.フィールド名}」: フィールドタイプ「${ft}」は nocode で対応していません`,
      );
    }
  }

  // SUBTABLE 内フィールドのバリデーション
  for (const field of data) {
    if (field.フィールドタイプ === "SUBTABLE" && field.options?.fields) {
      const subFields = field.options.fields as Record<
        string,
        {
          type?: string;
          label?: string;
          protocol?: string;
          options?: Record<string, unknown> & { options?: unknown[]; protocol?: string };
        }
      >;
      for (const [code, subField] of Object.entries(subFields)) {
        const label = subField.label || code;
        const tableName = field.フィールド名;

        // 禁止タイプチェック
        if (subField.type && FORBIDDEN_IN_SUBTABLE.has(subField.type)) {
          blockingErrors.push(
            `テーブル「${tableName}」内のフィールド「${label}」: ${subField.type} はテーブル内で使用できません`,
          );
        }

        // 選択肢系フィールドの options 必須チェック
        // UI形式: subField.options.options (nested), kintone形式: subField.options (flat)
        if (subField.type && SELECTION_FIELD_TYPES.has(subField.type)) {
          const nestedOpts = subField.options?.options;
          const hasNestedOptions = Array.isArray(nestedOpts) && nestedOpts.length > 0;
          const flatOpts = subField.options;
          const hasFlatOptions =
            flatOpts &&
            typeof flatOpts === "object" &&
            !Array.isArray(flatOpts) &&
            Object.keys(flatOpts).some(
              (k) =>
                ![
                  "noLabel",
                  "required",
                  "options",
                  "defaultSelectedOptions",
                  "defaultValue",
                  "order",
                ].includes(k),
            );
          if (!hasNestedOptions && !hasFlatOptions) {
            blockingErrors.push(
              `テーブル「${tableName}」内のフィールド「${label}」（${subField.type}）: 選択肢（options）が設定されていません`,
            );
          }
        }

        // LINK の protocol 自動補完（nocode sanitize_props 準拠）
        // UI形式: subField.options.protocol, kintone形式: subField.protocol
        if (subField.type === "LINK") {
          const protocol = subField.options?.protocol ?? subField.protocol;
          if (!protocol || !["WEB", "CALL", "MAIL"].includes(String(protocol))) {
            const hint = (label || code || "").toUpperCase();
            let inferred: string;
            if (["電話", "TEL", "携帯", "FAX", "ファックス"].some((kw) => hint.includes(kw))) {
              inferred = "CALL";
            } else if (["メール", "MAIL", "EMAIL", "Eメール"].some((kw) => hint.includes(kw))) {
              inferred = "MAIL";
            } else {
              inferred = "WEB";
            }
            // Write to both locations so it works regardless of data format
            if (subField.options) {
              subField.options.protocol = inferred;
            }
            subField.protocol = inferred;
          }
        }

        // 子フィールドのオプションバリデーション
        // SUBTABLE 子フィールドは flat props (minValue, maxLength 等) が直接、
        // 選択肢系の props (options, defaultSelectedOptions 等) が subField.options 内にあるため両方をマージ
        const childFieldOptions = {
          ...subField,
          ...(subField.options as Record<string, unknown>),
        } as unknown as FieldOptions;
        const childOptionErrors = validateFieldOptions(
          subField.type || "",
          childFieldOptions,
          label,
          `テーブル「${tableName}」内の`,
        );
        blockingErrors.push(...childOptionErrors);
      }
    }
  }

  // unique=true の SINGLE_LINE_TEXT は maxLength ≤ 64 でなければならない
  for (const field of data) {
    if (field.フィールドタイプ === "SINGLE_LINE_TEXT" && field.options?.unique === true) {
      const ml = field.options.maxLength;
      if (ml !== undefined && ml !== null && ml !== "") {
        const mlInt = Number.parseInt(String(ml), 10);
        if (!Number.isNaN(mlInt) && mlInt > 64) {
          blockingErrors.push(
            `「${field.フィールド名}」: 重複禁止フィールドの最大文字数は64文字以内にしてください（現在: ${mlInt}文字）`,
          );
        }
      }
    }
  }

  // フィールドオプションの kintone デプロイ時バリデーション
  for (const field of data) {
    const optionErrors = validateFieldOptions(
      field.フィールドタイプ,
      field.options,
      field.フィールド名,
    );
    blockingErrors.push(...optionErrors);
  }

  // 自アプリ側の参照フィールド存在チェック
  // LOOKUP の fieldMappings[].field、REFERENCE_TABLE の condition.field
  const fieldCodesByApp = new Map<string, Set<string>>();
  const selfAppSystemCodes = [
    "レコード番号",
    "作成者",
    "作成日時",
    "更新者",
    "更新日時",
    "ステータス",
    "作業者",
    "カテゴリー",
  ];
  for (const field of data) {
    let codes = fieldCodesByApp.get(field.app_id);
    if (!codes) {
      codes = new Set<string>(selfAppSystemCodes);
      fieldCodesByApp.set(field.app_id, codes);
    }
    codes.add(field.フィールドコード);
  }

  for (const field of data) {
    const selfAppCodes = fieldCodesByApp.get(field.app_id);
    if (!selfAppCodes) continue;

    // LOOKUP: コピー先フィールド (fieldMappings[].field) が自アプリに存在するか
    if (field.フィールドタイプ === "LOOKUP" && field.options?.lookup?.fieldMappings) {
      for (const mapping of field.options.lookup.fieldMappings) {
        if (mapping.field && !selfAppCodes.has(mapping.field)) {
          blockingErrors.push(
            `LOOKUP「${field.フィールド名}」のコピー先フィールド「${mapping.field}」が自アプリ内に存在しません`,
          );
        }
      }
    }

    // REFERENCE_TABLE: 条件フィールド (condition.field) が自アプリに存在するか
    if (field.フィールドタイプ === "REFERENCE_TABLE" && field.options?.referenceTable?.condition) {
      const condField = String(field.options.referenceTable.condition.field || "").trim();
      if (condField && !selfAppCodes.has(condField)) {
        blockingErrors.push(
          `関連レコード「${field.フィールド名}」の条件フィールド「${condField}」が自アプリ内に存在しません`,
        );
      }
    }
  }

  // フィールド名（ラベル）の長さチェック
  for (const field of data) {
    const label = (field.フィールド名 || "").trim();
    if (label.length > MAX_FIELD_LABEL_LENGTH) {
      blockingErrors.push(
        `「${label.slice(0, 20)}...」: フィールド名は${MAX_FIELD_LABEL_LENGTH}文字以内にしてください（現在: ${label.length}文字）`,
      );
    }
  }

  // フィールド数上限チェック（アプリ別）
  const appIds = Array.from(new Set(data.map((f) => f.app_id)));
  for (const appId of appIds) {
    const fieldsForApp = data.filter((f) => f.app_id === appId);
    const count = countFieldsForApp(fieldsForApp);
    if (count > MAX_FIELDS_PER_APP) {
      const appName = appData.find((a) => a.app_id === appId)?.app_name || appId;
      blockingErrors.push(
        `アプリ「${appName}」のフィールド数が上限を超えています（${count}/${MAX_FIELDS_PER_APP}）`,
      );
    }
  }

  // 選択肢系フィールドの options 必須チェック
  for (const field of data) {
    if (SELECTION_FIELD_TYPES.has(field.フィールドタイプ)) {
      const opts = field.options?.options;
      if (!opts || (typeof opts === "object" && Object.keys(opts).length === 0)) {
        blockingErrors.push(
          `「${field.フィールド名}」（${field.フィールドタイプ}）: 選択肢（options）が設定されていません`,
        );
      }
    }
  }

  // 選択肢 options の label/index バリデーション
  for (const field of data) {
    if (SELECTION_FIELD_TYPES.has(field.フィールドタイプ) && field.options?.options) {
      const opts = field.options.options;
      if (typeof opts === "object" && !Array.isArray(opts)) {
        const entries = Object.entries(opts as Record<string, { label?: string; index?: unknown }>);
        for (const [key, val] of entries) {
          if (typeof val !== "object" || val === null) continue;
          // key must match label
          const label = val.label;
          if (typeof label === "string" && label !== key) {
            blockingErrors.push(
              `「${field.フィールド名}」の選択肢: キー「${key}」と label「${label}」が一致しません`,
            );
          }
          // index must be string-formatted number
          const idx = val.index;
          if (idx !== undefined && idx !== null) {
            const idxStr = String(idx);
            if (Number.isNaN(Number(idxStr))) {
              blockingErrors.push(
                `「${field.フィールド名}」の選択肢「${key}」: index は数値文字列でなければなりません`,
              );
            }
          }
        }
      }
    }
  }

  // LINK フィールドの protocol を自動補完（nocode sanitize_props 準拠）
  // nocode は label/code からキーワード推定し、該当しなければ WEB をデフォルト設定する
  for (const field of data) {
    if (field.フィールドタイプ === "LINK" && field.options) {
      const protocol = field.options.protocol;
      if (!protocol || !["WEB", "CALL", "MAIL"].includes(String(protocol))) {
        const hint = (field.フィールド名 || field.フィールドコード || "").toUpperCase();
        if (["電話", "TEL", "携帯", "FAX", "ファックス"].some((kw) => hint.includes(kw))) {
          field.options.protocol = "CALL";
        } else if (["メール", "MAIL", "EMAIL", "Eメール"].some((kw) => hint.includes(kw))) {
          field.options.protocol = "MAIL";
        } else {
          field.options.protocol = "WEB";
        }
      }
    }
  }

  // NUMBER フィールドの digit を boolean に正規化（nocode sanitize_props 準拠）
  // 文字列 "true"/"false" 等はバックエンドから返却されうるため、ブロックせず自動変換する
  for (const field of data) {
    if (field.フィールドタイプ === "NUMBER" && field.options && "digit" in field.options) {
      const dv: unknown = field.options.digit;
      if (typeof dv === "boolean") {
        // OK
      } else if (typeof dv === "string") {
        const low = dv.trim().toLowerCase();
        if (["1", "true", "yes", "on"].includes(low)) {
          field.options.digit = true;
        } else if (["0", "false", "no", "off", ""].includes(low)) {
          field.options.digit = false;
        } else {
          blockingErrors.push(
            `「${field.フィールド名}」（NUMBER）: digit の値「${dv}」を boolean に変換できません`,
          );
        }
      } else {
        blockingErrors.push(
          `「${field.フィールド名}」（NUMBER）: digit は true または false でなければなりません`,
        );
      }
    }
  }

  // SUBTABLE の子フィールドが空でないかチェック
  for (const field of data) {
    if (field.フィールドタイプ === "SUBTABLE") {
      const subFields = field.options?.fields;
      if (!subFields || (typeof subFields === "object" && Object.keys(subFields).length === 0)) {
        blockingErrors.push(
          `テーブル「${field.フィールド名}」: 子フィールドが1件も設定されていません`,
        );
      }
    }
  }

  // SUBTABLE の子フィールドコードがアプリ内の他フィールドと重複していないかチェック
  for (const field of data) {
    if (field.フィールドタイプ === "SUBTABLE" && field.options?.fields) {
      const subFields = field.options.fields as Record<string, { code?: string }>;
      const appCodes = fieldCodesByApp.get(field.app_id);
      if (appCodes) {
        for (const [subCode, subField] of Object.entries(subFields)) {
          const code = subField.code || subCode;
          if (appCodes.has(code)) {
            blockingErrors.push(
              `テーブル「${field.フィールド名}」の子フィールド「${code}」がアプリ内の他フィールドと重複しています`,
            );
          }
        }
      }
    }
  }

  // LOOKUP の自アプリ参照禁止チェック
  for (const field of data) {
    if (field.フィールドタイプ === "LOOKUP" && field.options?.lookup?.relatedApp?.app) {
      const relatedApp = String(field.options.lookup.relatedApp.app).trim();
      const selfAppName = appData.find((a) => a.app_id === field.app_id)?.app_name;
      if (relatedApp === field.app_id || relatedApp === selfAppName) {
        blockingErrors.push(
          `LOOKUP「${field.フィールド名}」: 自アプリを参照先にすることはできません`,
        );
      }
    }
  }

  // LOOKUP fieldMappings の自己参照チェック（コピー先 = LOOKUP フィールド自身）
  for (const field of data) {
    if (field.フィールドタイプ === "LOOKUP" && field.options?.lookup?.fieldMappings) {
      for (const mapping of field.options.lookup.fieldMappings) {
        if (mapping.field && mapping.field === field.フィールドコード) {
          blockingErrors.push(
            `LOOKUP「${field.フィールド名}」: ルックアップフィールド自身をコピー先に指定できません。別のフィールドを指定してください`,
          );
        }
      }
    }
  }

  // LOOKUP コピー先フィールドの一意性チェック（同一アプリ内の複数LOOKUPが同じコピー先を持っていないか）
  for (const appId of appIds) {
    const usedDests = new Map<string, string>(); // destCode -> lookupFieldName
    for (const field of data) {
      if (field.app_id !== appId || field.フィールドタイプ !== "LOOKUP") continue;
      if (!field.options?.lookup?.fieldMappings) continue;
      for (const mapping of field.options.lookup.fieldMappings) {
        const dest = mapping.field;
        if (!dest) continue;
        const existing = usedDests.get(dest);
        if (existing) {
          blockingErrors.push(
            `LOOKUP「${field.フィールド名}」: コピー先「${dest}」は別のルックアップ「${existing}」と重複しています。1つのフィールドは1つのルックアップからのみコピーできます`,
          );
        } else {
          usedDests.set(dest, field.フィールド名);
        }
      }
    }
  }

  // LOOKUP コピー先が別の LOOKUP フィールドを指している場合を検出
  for (const field of data) {
    if (field.フィールドタイプ !== "LOOKUP" || !field.options?.lookup?.fieldMappings) continue;
    for (const mapping of field.options.lookup.fieldMappings) {
      const dest = mapping.field;
      if (!dest) continue;
      const destField = data.find((f) => f.app_id === field.app_id && f.フィールドコード === dest);
      if (destField && destField.フィールドタイプ === "LOOKUP") {
        blockingErrors.push(
          `LOOKUP「${field.フィールド名}」: コピー先「${dest}」はルックアップフィールドのため、コピー先に指定できません`,
        );
      }
    }
  }

  // LOOKUP 基底タイプの制限チェック
  for (const field of data) {
    if (field.フィールドタイプ === "LOOKUP" && field.options?.lookup?.relatedKeyField) {
      const relatedApp = String(field.options.lookup.relatedApp?.app || "").trim();
      if (relatedApp) {
        const relatedAppId = appData.find(
          (a) => a.app_id === relatedApp || a.app_name === relatedApp,
        )?.app_id;
        if (relatedAppId) {
          const relatedField = data.find(
            (f) =>
              f.app_id === relatedAppId &&
              f.フィールドコード === field.options?.lookup?.relatedKeyField,
          );
          if (relatedField) {
            const relatedType =
              relatedField.フィールドタイプ === "LOOKUP"
                ? "SINGLE_LINE_TEXT"
                : relatedField.フィールドタイプ;
            if (!LOOKUP_BASE_TYPES.has(relatedType) && relatedType !== "RECORD_NUMBER") {
              blockingErrors.push(
                `LOOKUP「${field.フィールド名}」: 参照キーのフィールドタイプは SINGLE_LINE_TEXT / NUMBER / CALC / LINK のみ対応です（${field.options.lookup.relatedKeyField}:${relatedType}）`,
              );
            }
          }
        }
      }
    }
  }

  // 致命的な参照エラーと安全にクリーンアップ可能な参照を分離
  const criticalReferences = invalidReferences.filter((ref) => ref.isCritical);
  const safeCleanableReferences = invalidReferences.filter((ref) => !ref.isCritical);

  // 警告メッセージを生成
  const warnings: string[] = [];

  if (deletedFields.length > 0) {
    warnings.push(`${deletedFields.length}件のフィールドが削除されます`);
  }

  if (criticalReferences.length > 0) {
    warnings.push(`${criticalReferences.length}件の参照フィールドが無効になるため削除が必要です`);
  }

  if (safeCleanableReferences.length > 0) {
    warnings.push(`${safeCleanableReferences.length}件の参照設定がクリアされます`);
  }

  if (blockingErrors.length > 0) {
    warnings.push(`${blockingErrors.length}件の設定エラーがあり保存できません`);
  }

  return {
    isValid: invalidReferences.length === 0 && blockingErrors.length === 0,
    blockingErrors,
    invalidReferences,
    criticalReferences,
    safeCleanableReferences,
    deletedFields,
    warnings,
    shouldBlockSave: criticalReferences.length > 0 || blockingErrors.length > 0,
    normalizedData: data,
  };
}

/**
 * 安全にクリーンアップ可能な参照のみをクリーンアップする
 * 致命的な参照（根幹の参照が壊れている）は触らない
 *
 * 安全にクリーンアップ可能:
 * - LOOKUP: fieldMappings, lookupPickerFields
 * - REFERENCE_TABLE: displayFields
 *
 * 致命的（クリーンアップ不可、フィールド削除が必要）:
 * - LOOKUP: relatedKeyField
 * - REFERENCE_TABLE: condition.relatedField
 */
export function cleanupSafeReferences(
  data: FieldData[],
  safeCleanableReferences: InvalidReference[],
): CleanupResult {
  const cleanedData = data.map((field) => ({ ...field }));
  const safelyCleanedFields: FieldData[] = [];

  safeCleanableReferences.forEach((ref) => {
    // 致命的な参照は処理しない
    if (ref.isCritical) return;

    const fieldIndex = cleanedData.findIndex((f) => f["id"] === ref.sourceField["id"]);
    if (fieldIndex === -1) return;

    const field = cleanedData[fieldIndex];
    if (!field || !field.options) return;

    const deletedCodes = new Set(ref.deletedFieldCodes);
    let wasModified = false;

    if (ref.referenceType === "LOOKUP" && field.options.lookup) {
      const lookup = { ...field.options.lookup };

      // 補助的な参照のみクリーンアップ（根幹の relatedKeyField は触らない）
      // fieldMappings から削除されたフィールドを除去
      if (lookup.fieldMappings) {
        const originalLength = lookup.fieldMappings.length;
        lookup.fieldMappings = lookup.fieldMappings.filter(
          (mapping) => !deletedCodes.has(mapping.relatedField),
        );
        if (lookup.fieldMappings.length !== originalLength) {
          wasModified = true;
        }
      }

      // lookupPickerFields から削除されたフィールドを除去
      if (lookup.lookupPickerFields) {
        const originalLength = lookup.lookupPickerFields.length;
        lookup.lookupPickerFields = lookup.lookupPickerFields.filter(
          (code) => !deletedCodes.has(code),
        );
        if (lookup.lookupPickerFields.length !== originalLength) {
          wasModified = true;
        }
      }

      if (wasModified) {
        cleanedData[fieldIndex] = {
          ...field,
          options: { ...field.options, lookup },
        };
        safelyCleanedFields.push(cleanedData[fieldIndex]);
      }
    }

    if (ref.referenceType === "REFERENCE_TABLE" && field.options.referenceTable) {
      const refTable = { ...field.options.referenceTable };

      // 補助的な参照のみクリーンアップ（根幹の condition.relatedField は触らない）
      // displayFields から削除されたフィールドを除去
      if (refTable.displayFields) {
        const originalLength = refTable.displayFields.length;
        refTable.displayFields = refTable.displayFields.filter((code) => !deletedCodes.has(code));
        if (refTable.displayFields.length !== originalLength) {
          wasModified = true;
        }
      }

      if (wasModified) {
        cleanedData[fieldIndex] = {
          ...field,
          options: { ...field.options, referenceTable: refTable },
        };
        safelyCleanedFields.push(cleanedData[fieldIndex]);
      }
    }
  });

  return {
    data: cleanedData,
    fieldsToDelete: [], // この関数では削除対象は返さない
    safelyCleanedFields,
  };
}

/**
 * 致命的な参照エラーを持つフィールドを削除する
 */
export function deleteCriticalFields(
  data: FieldData[],
  criticalReferences: InvalidReference[],
): CleanupResult {
  const fieldIdsToDelete = new Set(criticalReferences.map((ref) => ref.sourceField["id"]));

  const fieldsToDelete = data.filter((f) => fieldIdsToDelete.has(f["id"]));
  const cleanedData = data.filter((f) => !fieldIdsToDelete.has(f["id"]));

  return {
    data: cleanedData,
    fieldsToDelete,
    safelyCleanedFields: [],
  };
}

/**
 * 無効な参照を持つフィールドのoptionsをクリーンアップ
 * 後方互換性のために残している - 新しいコードでは cleanupSafeReferences を使用すること
 * @deprecated cleanupSafeReferences と deleteCriticalFields を使用してください
 */
export function cleanupInvalidReferences(
  data: FieldData[],
  invalidReferences: InvalidReference[],
): FieldData[] {
  // 安全にクリーンアップ可能な参照のみ処理
  const safeRefs = invalidReferences.filter((ref) => !ref.isCritical);
  const result = cleanupSafeReferences(data, safeRefs);
  return result.data;
}

/**
 * アプリ生成前チェック: LOOKUP fieldMappings の型不整合を検出する（ゲート）
 *
 * フィールド設計書の JSON 文字列を受け取り、LOOKUP フィールドの fieldMappings に
 * 型互換性のないコピー先↔コピー元の組み合わせがある場合にブロッキングメッセージ配列を返す。
 *
 * 誤ブロック防止:
 *   - JSON 不正・型解決不能・参照先未解決などはスキップして空配列を返す（バックエンドに委ねる）
 *   - `FIELD_MAPPING_COMPATIBLE_SOURCES` にコピー先タイプが存在しない場合もスキップ
 *
 * @param fieldDesignContent - フィールド設計書の JSON 文字列（content）
 * @returns ブロッキングメッセージの配列（空配列 = 問題なし or 検査不能）
 */
export function validateLookupTypesForGeneration(fieldDesignContent: string): string[] {
  // --- 入力パース（パース失敗時は誤ブロックしない）---
  if (!fieldDesignContent || typeof fieldDesignContent !== "string") {
    return [];
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(fieldDesignContent);
  } catch {
    // マークダウン形式などパース不能なコンテンツは誤ブロックしない
    return [];
  }

  // JSON 文字列としてエスケープされたコンテンツは非対応
  if (typeof parsed === "string") {
    return [];
  }

  if (
    typeof parsed !== "object" ||
    parsed === null ||
    !Array.isArray((parsed as Record<string, unknown>)["apps"])
  ) {
    return [];
  }

  const apps = (parsed as { apps: unknown[] })["apps"];

  // --- アプリ識別子 → app_id のマップ構築 ---
  const appIdByIdentifier = new Map<string, string>();
  for (const app of apps) {
    if (typeof app !== "object" || app === null) continue;
    const a = app as Record<string, unknown>;
    const appId = typeof a["app_id"] === "string" ? a["app_id"].trim() : null;
    const appName = typeof a["app_name"] === "string" ? a["app_name"].trim() : null;
    if (appId) {
      appIdByIdentifier.set(appId, appId);
      if (appName) {
        appIdByIdentifier.set(appName, appId);
      }
    }
  }

  // --- app_id → (fieldCode → { rawType, lookupOptions }) のマップ構築 ---
  // properties[].required.rows[0] = [app_id, fieldCode, label|null, fieldType]
  // LOOKUP フィールドのベース型解決のために lookup オプションをそのまま保持する
  // （後段の resolveEffectiveType で relatedKeyField のタイプから推論する）
  type RawFieldInfo = { rawType: string; lookupOptions: Record<string, unknown> | null };
  const fieldRawByAppAndCode = new Map<string, Map<string, RawFieldInfo>>();
  for (const app of apps) {
    if (typeof app !== "object" || app === null) continue;
    const a = app as Record<string, unknown>;
    const appId = typeof a["app_id"] === "string" ? a["app_id"].trim() : null;
    if (!appId) continue;

    const codeMap = new Map<string, RawFieldInfo>();
    fieldRawByAppAndCode.set(appId, codeMap);

    const properties = Array.isArray(a["properties"]) ? a["properties"] : [];
    for (const prop of properties) {
      if (typeof prop !== "object" || prop === null) continue;
      const p = prop as Record<string, unknown>;

      const rows =
        typeof p["required"] === "object" &&
        p["required"] !== null &&
        Array.isArray((p["required"] as Record<string, unknown>)["rows"])
          ? ((p["required"] as Record<string, unknown>)["rows"] as unknown[])
          : [];

      if (rows.length === 0) continue;
      const row = rows[0];
      if (!Array.isArray(row) || row.length < 4) continue;

      const fieldCode = typeof row[1] === "string" ? row[1].trim() : null;
      const rawFieldType = typeof row[3] === "string" ? row[3].trim() : null;
      if (!fieldCode || !rawFieldType) continue;

      // LOOKUP オプションを保持（ベース型推論は resolveEffectiveType で実施）
      const options =
        typeof p["options"] === "object" && p["options"] !== null
          ? (p["options"] as Record<string, unknown>)
          : null;
      const lookupOptions =
        options !== null && typeof options["lookup"] === "object" && options["lookup"] !== null
          ? (options["lookup"] as Record<string, unknown>)
          : null;
      codeMap.set(fieldCode, { rawType: rawFieldType, lookupOptions });
    }
  }

  // システムフィールドコード → タイプのマップ（参照先解決に使用）
  const systemFieldTypes: Record<string, string> = {
    レコード番号: "RECORD_NUMBER",
    作成者: "CREATOR",
    作成日時: "CREATED_TIME",
    更新者: "MODIFIER",
    更新日時: "UPDATED_TIME",
    ステータス: "STATUS",
    作業者: "STATUS_ASSIGNEE",
    カテゴリー: "CATEGORY",
  };

  const resolveAppId = (identifier: string | undefined): string | null => {
    const key = typeof identifier === "string" ? identifier.trim() : "";
    if (!key) return null;
    return appIdByIdentifier.get(key) ?? null;
  };

  /**
   * フィールドの実効型を返す（raw JSON 版）。
   * LOOKUP フィールドの場合は relatedKeyField のタイプからベース型を推論する。
   * これは既存の inferLookupBaseType(662-688 行) と同じ解決ルールの raw JSON 版。
   *   NUMBER / RECORD_NUMBER / CALC → "NUMBER"
   *   SINGLE_LINE_TEXT / LINK       → "SINGLE_LINE_TEXT"
   *   それ以外 / 解決不能            → null（誤ブロックしないためスキップ）
   */
  const resolveEffectiveType = (appId: string, code: string): string | null => {
    const c = typeof code === "string" ? code.trim() : "";
    if (!c) return null;
    // システムフィールドを先にチェック
    const sys = systemFieldTypes[c];
    if (sys) return sys;

    const info = fieldRawByAppAndCode.get(appId)?.get(c);
    if (!info) return null;

    // LOOKUP フィールドでない場合はそのまま返す
    if (info.lookupOptions === null) return info.rawType;

    // LOOKUP の場合: relatedKeyField の型からベース型を推論
    // （inferLookupBaseType / getFieldType(662-688 行)と同じ解決ルールの raw JSON 版）
    const relatedAppRaw =
      typeof info.lookupOptions["relatedApp"] === "object" &&
      info.lookupOptions["relatedApp"] !== null
        ? (info.lookupOptions["relatedApp"] as Record<string, unknown>)
        : null;
    const relatedAppIdentifier =
      relatedAppRaw !== null && typeof relatedAppRaw["app"] === "string"
        ? relatedAppRaw["app"]
        : null;
    const relatedKeyField =
      typeof info.lookupOptions["relatedKeyField"] === "string"
        ? info.lookupOptions["relatedKeyField"].trim()
        : null;

    if (!relatedAppIdentifier || !relatedKeyField) return null;
    const relatedAppId = resolveAppId(relatedAppIdentifier);
    if (!relatedAppId) return null;

    // relatedKeyField 自体も LOOKUP の可能性があるため resolveEffectiveType を再帰しない
    // （無限ループ防止・過剰解決防止）。raw タイプだけで判定する。
    const keyFieldInfo = fieldRawByAppAndCode.get(relatedAppId)?.get(relatedKeyField);
    const keyType = keyFieldInfo?.rawType ?? systemFieldTypes[relatedKeyField] ?? null;
    if (!keyType) return null;

    if (keyType === "NUMBER" || keyType === "RECORD_NUMBER" || keyType === "CALC") return "NUMBER";
    if (keyType === "SINGLE_LINE_TEXT" || keyType === "LINK") return "SINGLE_LINE_TEXT";
    // 解決不能な LOOKUP は null を返してスキップ（誤ブロック防止）
    return null;
  };

  // --- LOOKUP fieldMappings 型互換性チェック ---
  const blockingErrors: string[] = [];

  for (const app of apps) {
    if (typeof app !== "object" || app === null) continue;
    const a = app as Record<string, unknown>;
    const appId = typeof a["app_id"] === "string" ? a["app_id"].trim() : null;
    if (!appId) continue;

    const properties = Array.isArray(a["properties"]) ? a["properties"] : [];

    for (const prop of properties) {
      if (typeof prop !== "object" || prop === null) continue;
      const p = prop as Record<string, unknown>;

      const options =
        typeof p["options"] === "object" && p["options"] !== null
          ? (p["options"] as Record<string, unknown>)
          : null;
      if (!options) continue;

      const lookup =
        typeof options["lookup"] === "object" && options["lookup"] !== null
          ? (options["lookup"] as Record<string, unknown>)
          : null;
      if (!lookup) continue;

      // このフィールドが LOOKUP であることを確認（rows[0][3] が存在）
      const rows =
        typeof p["required"] === "object" &&
        p["required"] !== null &&
        Array.isArray((p["required"] as Record<string, unknown>)["rows"])
          ? ((p["required"] as Record<string, unknown>)["rows"] as unknown[])
          : [];
      if (rows.length === 0) continue;
      const row = rows[0];
      if (!Array.isArray(row)) continue;

      const fieldName = typeof p["label"] === "string" ? p["label"] : "（名前不明）";

      // 参照先アプリを解決（解決できない場合は誤ブロックしない）
      const relatedApp =
        typeof lookup["relatedApp"] === "object" && lookup["relatedApp"] !== null
          ? (lookup["relatedApp"] as Record<string, unknown>)
          : null;
      const relatedAppIdentifier =
        relatedApp !== null && typeof relatedApp["app"] === "string" ? relatedApp["app"] : null;
      const relatedAppId = resolveAppId(relatedAppIdentifier ?? undefined);
      if (!relatedAppId) {
        // 参照先アプリが解決できない場合は誤ブロックしない
        continue;
      }

      // fieldMappings の型互換性チェック
      const fieldMappings = Array.isArray(lookup["fieldMappings"])
        ? (lookup["fieldMappings"] as unknown[])
        : [];

      for (const mapping of fieldMappings) {
        if (typeof mapping !== "object" || mapping === null) continue;
        const m = mapping as Record<string, unknown>;

        const destCode = typeof m["field"] === "string" ? m["field"].trim() : null;
        const srcCode = typeof m["relatedField"] === "string" ? m["relatedField"].trim() : null;
        if (!destCode || !srcCode) continue;

        const destType = resolveEffectiveType(appId, destCode);
        const srcType = resolveEffectiveType(relatedAppId, srcCode);

        // どちらかの型が解決できない場合は誤ブロックしない（バックエンドに委ねる）
        // LOOKUP でベース型推論不能の場合も null が返りスキップされる
        if (!destType || !srcType) continue;

        const allowed = FIELD_MAPPING_COMPATIBLE_SOURCES.get(destType);
        // destType が互換マトリクスに存在しない場合はスキップ（誤ブロック防止）
        if (!allowed) continue;

        if (!allowed.has(srcType)) {
          blockingErrors.push(
            `ルックアップ「${fieldName}」：\n　コピー元「${srcCode}」（${formatFieldType(srcType)}）\n　→ コピー先「${destCode}」（${formatFieldType(destType)}）にはコピーできません\n　　コピー先を次のいずれかに変更: ${formatCompatibleDestinations(srcType)}`,
          );
        }
      }
    }
  }

  return blockingErrors;
}

/**
 * バリデーション結果から警告ダイアログ用のメッセージを生成
 */
export function generateWarningMessage(result: ValidationResult): string {
  const messages: string[] = [];

  if (result.deletedFields.length > 0) {
    messages.push("以下のフィールドが削除されます:");
    result.deletedFields.forEach((df) => {
      messages.push(`  - ${df.fieldName} (${df.fieldCode})`);
    });
    messages.push("");
  }

  if (result.invalidReferences.length > 0) {
    messages.push("以下の参照が無効になり、参照設定がクリアされます:");
    result.invalidReferences.forEach((ref) => {
      messages.push(`  - ${ref.message}`);
    });
    messages.push("");
  }

  messages.push("保存を続行しますか？");

  return messages.join("\n");
}
